import { IntlShape } from 'react-intl';
import { FYMAPIError } from 'fym-common/src/generics/errors/FYMAPIError';
import { Sentry } from '../../../errorBoundary/sentry';
import { studioMessages } from './studioMessages';

/** What the Studio shows next to the button of an action: an error, or a plain note (busy). */
export interface StudioActionFeedback {
  tone: 'danger' | 'info';
  message: string;
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return typeof value === 'object' && value !== null ? (value as Record<string, unknown>) : null;
}

/**
 * The `M24_*` code of an API error: `cause.code`, else `cause.reason` (`customBaseQuery` puts the
 * REST `cause` on the error as `m24Limit`). Null for an error with no such code: a Joi 400 ("…
 * is not allowed"), a 5xx, a network error.
 */
export function studioErrorCode(error: unknown): string | null {
  const cause = asRecord(asRecord(error)?.m24Limit);
  for (const value of [cause?.code, cause?.reason]) {
    if (typeof value === 'string' && value.startsWith('M24_')) {
      return value;
    }
  }
  return null;
}

/**
 * The sentence for a failed Studio action. A refusal with an `M24_*` code keeps the sentence of
 * the server (for example "Add the lyrics of the song first."). Any other error gets the general
 * sentence: the raw text ("'items[0].params.lineFrom' is not allowed") goes only to the console
 * and Sentry (`reportStudioActionError`).
 */
export function studioActionErrorMessage(intl: IntlShape, error: unknown): string {
  if (studioErrorCode(error) !== null && error instanceof FYMAPIError && error.isOperational) {
    const message = error.message?.trim() ?? '';
    if (message !== '') {
      return message;
    }
  }
  return intl.formatMessage(studioMessages.actionFailed);
}

/**
 * Logs the technical text of a failed Studio action. An error with no `M24_*` code is a bug or an
 * outage on our side, so it also goes to Sentry.
 */
export function reportStudioActionError(action: string, error: unknown): void {
  console.error(`Studio: ${action} failed`, error);
  if (studioErrorCode(error) === null) {
    Sentry.captureException(error, { tags: { studioAction: action } });
  }
}

/** The feedback of a failed Studio action: reports it and gives the sentence to show. */
export function studioActionErrorFeedback(intl: IntlShape, action: string, error: unknown): StudioActionFeedback {
  reportStudioActionError(action, error);
  return { tone: 'danger', message: studioActionErrorMessage(intl, error) };
}
